import {EGeoType} from "./IconService";
import {AppConfig} from "../utils/AppConfig";
import {GeoJSON} from "ol/format";
import {Geometry} from "ol/geom";
import {Feature} from "ol";
import {cats} from "../utils/setup";
import {IThemegroup} from "../interfaces/ICategoryItem";
import GeoFAMemoizer, {GeoFAMemoizeRecordType} from "./GeoFAMemoizer";
import {cacheService} from "./CacheService";

const ENDPOINT_URL = AppConfig.apiBaseUrl

// Enum format, geoformat
interface IGeoFAQuery {
    q: string
    format?: string
    geoformat?: string
}

// TODO - remove doublette
interface IParams {
    kommuneGUID?: string
}

class GeoFAService {

    kommunenr: number[] | undefined = undefined;

    constructor(kommunenr: number[]) {
        this.kommunenr = kommunenr;
        // console.log('geofaservice', this.kommunenr);
    }

    /**
     * Get the memoizer from the global cache service
     */
    private getMemoizer(): GeoFAMemoizer | null {
        return cacheService.getMemoizer();
    }

    PHOTO_URL = AppConfig.photoBaseUrl;

    THEME_POINTS = "fkg.t_5800_fac_pkt";
    THEME_ROUTES = "fkg.t_5802_fac_li";
    THEME_POLYGONS = "fkg.t_5801_fac_fl";

    POINT_RETURN_FIELDS = [
        "objekt_id",
        "navn",
        "facil_ty",
        "facil_ty_k",
        "kommunekode",
        "geometri",
        "beskrivels", // TODO: move to DetailPage.tsx
        "lang_beskr", // TODO: move to DetailPage.tsx
        "foto_link",
        "geofafoto",
        "temakode",
        "gppx_link"
    ]

    // The minimum set of values we need for all locations apart from the Detail View.
    MIN_FIELDS_5800 = [
        "geometri",
        "objekt_id",
        "facil_ty_k",
        "facil_ty",
        "temakode",
        "navn",
        "navn_d",
        "navn_uk",
        "off_kode"
    ]

    MIN_FIELDS_5802 = [
        "geometri",
        "objekt_id",
        "rute_ty_k",
        "beskrivels",
        "temakode",
        "navn",
        "off_kode",
        "gpx_link",
        "rute_uty_k"
    ]

    //
    MIN_FIELDS_5801 = [
        "geometri",
        "objekt_id",
        "facil_ty_k",
        "facil_ty",
        "beskrivels",
        "temakode",
        "navn",
        "off_kode",
    ]


    MIN_FIELDS_5607 = [
        "geometri",
        "objekt_id",
        "temakode",
        "ladefacilitet_type_kode"
    ]
    ALL_POINT_RETURN_FIELDS = [
        '*'
    ]
    /*
    Return a list of class names (geoFA points) from the filter configuration
     */
    getFacilityClassesFromCats():number[] {
        const result: number[] = [];

        // Helper function to recursively extract numbers from themes
        const extractThemes = (themes: (number | IThemegroup)[]): number[] => {
            const numbers: number[] = [];

            themes.forEach(theme => {
                if (typeof theme === 'number') {
                    numbers.push(theme);
                } else if (theme && typeof theme === 'object' && 'themes' in theme) {
                    // It's an IThemegroup object, recursively get its themes
                    numbers.push(...extractThemes(theme.themes));
                }
            });

            return numbers;
        };

        // Filter cats where geotype is EGeoType.Point
        cats.forEach(cat => {
            if (cat.geotype === EGeoType.Point && cat.themes) {
                result.push(...extractThemes(cat.themes));
            }
        });

        // Remove duplicates and sort ascending
        return Array.from(new Set(result)).sort((a, b) => a - b);
    }

    getIN(keys: number[] | undefined): string {
        if (keys) {
            return `IN (${keys.join(', ')})`;
        } else {
            return '';
        }

    }

    knr () {
        return this.kommunenr
    }

    public dictToURI(dict: object) {
        let s = "";
        Object.entries(dict).forEach(
            ([key, value]) => {
                s = String(s) + "&" + key + "=" + value;
            }
        );
        return s.substring(1);
    }

    public async getLadestanderFeatures<TResponse>(type?: EGeoType, fields?: string[]): Promise<TResponse> {

        let flds = fields || this.MIN_FIELDS_5607;

        let sql:string= '';

        const schema = "fkg";

        sql = `SELECT ${flds.join(',')} FROM ${schema}.${type} WHERE beliggenhedskommune ${this.getIN(this.knr())} AND ladefacilitet_type_kode=2`;

        const QUERY= {
            q: encodeURI(sql),
            format: 'geojson',
            geoformat: 'geojson',
            srs: AppConfig.srs
        }

        let queryString = ENDPOINT_URL + '?' + this.dictToURI(QUERY);

        return fetch(queryString)
            .then((resp) => resp.json())
            .then((data) => {

                // Doing this by a query did not work, so we filter old school
                const rawFeatures: Feature<Geometry>[] = new GeoJSON().readFeatures(data);
                const ftrs: Feature<Geometry>[] = []

                rawFeatures.forEach((f) => {
                    const pp = f.getProperties();
                    pp.facil_ty_k = 888;
                    f.setProperties(pp);
                    ftrs.push(f);
                });

                return ftrs as TResponse;
            })
            .catch(e => {
                return Promise.reject({
                    status: e.status,
                    ok: false,
                    message: e.json,
                    body: e.json
                });
            });
    }

    public async getFeatures<TResponse>(type?: EGeoType, fields?: string[]): Promise<TResponse> {

        // Fallback to minimal
        let flds = fields || this.MIN_FIELDS_5800;

        let sql:string= '';

        const schema = "fkg_foto_gpx_link";

        if (type === EGeoType.Point) {

            const facilityPoints = this.getFacilityClassesFromCats();

            if (AppConfig.FACILITY_POINTS && AppConfig.FACILITY_POINTS.length > 0) {
                sql = `SELECT ${flds.join(',')} FROM ${schema}.${type} WHERE beliggenhedskommune ${this.getIN(this.knr())} AND facil_ty_k ${this.getIN(AppConfig.FACILITY_POINTS)}`;
            } else {
                sql = `SELECT ${flds.join(',')} FROM ${schema}.${type} WHERE beliggenhedskommune ${this.getIN(this.knr())}`;
            }
        }

        if (type === EGeoType.Line) {
            let schema = "fkg_foto_gpx_link";
            if (AppConfig.FACILITY_LINES && AppConfig.FACILITY_LINES.length > 0) {
                sql = `SELECT ${flds.join(',')} FROM fk${schema}.${type} WHERE beliggenhedskommune ${this.getIN(this.knr())} AND rute_ty_k ${this.getIN(AppConfig.FACILITY_LINES)}`;
            } else {
                sql = `SELECT ${flds.join(',')} FROM ${schema}.${type} WHERE beliggenhedskommune ${this.getIN(this.knr())}`;
            }
        }

        // TODO: Line > Polygons ?
        if (type === EGeoType.Polygon) {
            let schema = "fkg_foto_gpx_link";
            if (AppConfig.FACILITY_POLYGONS && AppConfig.FACILITY_POLYGONS.length > 0) {
                sql = `SELECT ${flds.join(',')} FROM ${schema}.${type} WHERE beliggenhedskommune ${this.getIN(this.knr())} AND facil_ty_k ${this.getIN(AppConfig.FACILITY_POLYGONS)}`;
            } else {
                sql = `SELECT ${flds.join(',')} FROM ${schema}.${type} WHERE beliggenhedskommune ${this.getIN(this.knr())}`;
            }
        }

        const QUERY= {
            q: encodeURI(sql),
            format: 'geojson',
            geoformat: 'geojson',
            srs: AppConfig.srs
        }

        let queryString = ENDPOINT_URL + '?' + this.dictToURI(QUERY);

        return fetch(queryString)
            .then((resp) => resp.json())
            .then((data) => {

                // Doing this by a query did not work, so we filter old school
                const rawFeatures: Feature<Geometry>[] = new GeoJSON().readFeatures(data);
                const ftrs: Feature<Geometry>[] = []

                rawFeatures.forEach((f) => {
                    if (f.getProperties().off_kode == 1) {
                        if (f.getProperties().rute_uty_k === 17) {
                            const pp = f.getProperties();
                            pp.rute_ty_k = 666;
                            f.setProperties(pp);
                            ftrs.push(f);
                        } else {
                            ftrs.push(f);
                        }
                    }
                });

                return ftrs as TResponse;
            })
            .catch(e => {
                return Promise.reject({
                    status: e.status,
                    ok: false,
                    message: e.json,
                    body: e.json
                });
            });
    }

    /**
     * Return a feature from geoFA
     * @param objectID - an objects objekt_id
     * @param type  - the type in fkg
     */
    public async getSingleFeatureFromGeoFA<TResponse>(objectID: string, type?: EGeoType):
        Promise<TResponse> {

        // Ensure cache service is initialized
        /*
        if (!cacheService.isInitialized()) {
            await cacheService.initialize();
        }
         */

        const geoJsonFormat = new GeoJSON();
        const memoizer = this.getMemoizer();

        // Try to get from cache first (using MAXIMAL cache type for full feature data)
        if (memoizer) {
            try {
                const cachedFeature = await memoizer.getFeature(objectID, GeoFAMemoizeRecordType.MAXIMAL);
                if (cachedFeature) {
                    // Convert cached OpenLayers feature back to GeoJSON format
                    const geoJsonFeature = geoJsonFormat.writeFeatureObject(cachedFeature, {
                        featureProjection: 'EPSG:3857',
                        dataProjection: 'EPSG:4326'
                    });

                    // Return in the same format as the API response (FeatureCollection)
                    return {
                        type: 'FeatureCollection',
                        features: [geoJsonFeature]
                    } as TResponse;
                }
            } catch (error) {
                console.warn('Error reading from cache, fetching from API:', error);
            }
        }

        // Cache miss - fetch from API
        let sql;

        if (type===EGeoType.LadestanderPoint) {
            const schema = "fkg";
            sql = `SELECT * FROM ${schema}.${type} WHERE objekt_id='${objectID}'`;
        } else {
            sql = `SELECT * FROM fkg_foto_gpx_link.${type} WHERE objekt_id='${objectID}'`;
        }

        // TODO: add geotype lookup
        const QUERY: IGeoFAQuery = {
            q: encodeURI(sql),
            format: 'geojson',
            geoformat: 'geojson'
        }

        let queryString = ENDPOINT_URL + '?' + this.dictToURI(QUERY);

        const resp = await fetch(queryString);
        const data = await resp.json();

        // Remove all keys where value is null
        const removeNullValues = (obj: any): any => {
            if (obj === null || obj === undefined) {
                return obj;
            }

            if (Array.isArray(obj)) {
                return obj.map(item => removeNullValues(item));
            }

            if (typeof obj === 'object') {
                const cleaned: any = {};
                for (const [key, value] of Object.entries(obj)) {
                    if (value !== null) {
                        cleaned[key] = removeNullValues(value);
                    }
                }
                return cleaned;
            }

            return obj;
        };

        const cleanedData = removeNullValues(data);

        console.log('cleanedData', cleanedData);

        // Cache the fetched feature for future use
        if (memoizer && cleanedData.features && cleanedData.features.length > 0) {
            try {
                // Parse the first feature from the GeoJSON response
                const features = geoJsonFormat.readFeatures(cleanedData, {
                    featureProjection: 'EPSG:3857',
                    dataProjection: 'EPSG:4326'
                });

                if (features.length > 0) {
                    const feature = features[0];
                    // Set the objectID as the feature ID if not already set
                    if (!feature.getId()) {
                        feature.setId(objectID);
                    }
                    // Set geotype property for caching
                    if (type) {
                        feature.set('geotype', type);
                    }
                    // Cache using MAXIMAL type since this is full feature data
                    await memoizer.saveFeature(feature, GeoFAMemoizeRecordType.MAXIMAL);
                }
            } catch (error) {
                console.warn('Error caching feature:', error);
                // Continue even if caching fails
            }
        }

        return cleanedData as TResponse;

    }

    public async getFeaturesForUIDList<TResponse> (ll: string[], type?: EGeoType, fields?: string[]): Promise<TResponse> {


        const quotedList = ll.map(item => `'${item}'`);
        const concatenatedString = quotedList.join(',');
        const sql= `SELECT * FROM fkg.t_5802_fac_li WHERE objekt_id IN (${concatenatedString})`;

        const QUERY: IGeoFAQuery = {
            q: encodeURI(sql),
            format: 'geojson',
            geoformat: 'geojson'
        }

        let queryString = ENDPOINT_URL + '?' + this.dictToURI(QUERY);

        return fetch(queryString)
            .then((resp) => resp.json())
            .then((data) => {
                return new GeoJSON().readFeatures(data) as TResponse;
            })
            .catch(e => {
                return Promise.reject({
                    status: e.status,
                    ok: false,
                    message: e.json,
                    body: e.json
                });
            });

    }

    /*
    Let's get all photo connections to our object.
     */
    public async getPhotosForFeature<TResponse>(objekt_id: string): Promise<TResponse> {

        const sql = `SELECT * FROM t_7900_fotoforbindelse WHERE foto_objek='${objekt_id}'`;

        let QUERY: IGeoFAQuery = {
            q: encodeURI(sql)
        }

        let queryString = `${ENDPOINT_URL}?${this.dictToURI(QUERY)}`;

        return fetch(queryString)
            .then((resp) => resp.json())
            .then((data) => {
                return new GeoJSON().readFeatures(data) as TResponse
            })
            .catch(e => {
                return Promise.reject({
                    status: e.status,
                    ok: false,
                    message: e.json,
                    body: e.json
                });
            });
    }

    /*
    This rather exotic call returns a list of objekt_id's where photos are attached to.
    We need this for the height our modal opens upwards -- so we do not expose the headline.
    Contains duplicates
     */
    public async getFeatureIDsWithPhotosAttached<TResponse>(): Promise<TResponse> {

        const sql = `SELECT foto_objek FROM t_7900_fotoforbindelse WHERE kommunekode=${this.getIN(this.knr())} OR beliggenhedskommune=${this.getIN(this.knr())};`;

        const QUERY: IGeoFAQuery = {
            q: encodeURI(sql)
        }

        let queryString = ENDPOINT_URL + '?' + this.dictToURI(QUERY)

        return fetch(queryString)
            .then((resp) => resp.json())
            .then((data) => data as TResponse)
            .catch(e => {
                return Promise.reject({
                    status: e.status,
                    ok: false,
                    message: e.json,
                    body: e.json
                });
            });
    }
}

export default GeoFAService;
